import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenantCurrency } from '../context/TenantCurrencyContext';
import { apiService } from '../services/api';
import { getHostTenantId, getSessionTenantId } from '../config/api-config';
import { formatApiError } from '../utils/apiError';
import { roleLabel } from '../utils/roles';
import type { User } from '../types/config';
import type { StaffSummary } from '../services/api/types/common';
import type { StudentPortalProfile } from '../services/api/types/portal';

type UserProfilePageProps = {
  user: User;
};

function profileInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-secondary-500">{label}</dt>
      <dd className="mt-1 text-sm font-medium text-secondary-900 break-words">{value || '—'}</dd>
    </div>
  );
}

export function UserProfilePage({ user }: UserProfilePageProps) {
  const history = useHistory();
  const { logout } = useAuth();
  const { tenant, loading: tenantLoading } = useTenantCurrency();

  const [staffRecord, setStaffRecord] = useState<StaffSummary | null>(null);
  const [studentProfile, setStudentProfile] = useState<StudentPortalProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [resetLinkSending, setResetLinkSending] = useState(false);

  const initials = useMemo(() => profileInitials(user.name), [user.name]);

  useEffect(() => {
    let cancelled = false;

    async function loadRoleDetails() {
      if (user.role === 'super_admin') return;

      setProfileLoading(true);
      setProfileError(null);

      try {
        if (user.role === 'student') {
          const res = await apiService.getStudentPortalProfile();
          if (!cancelled) setStudentProfile(res.data);
          return;
        }

        const res = await apiService.listStaff({ search: user.email, limit: 20 });
        const match =
          res.data?.staff?.find(
            (row) => (row.email ?? '').trim().toLowerCase() === user.email.trim().toLowerCase()
          ) ?? res.data?.staff?.[0] ?? null;
        if (!cancelled) setStaffRecord(match);
      } catch (error) {
        if (!cancelled) {
          setProfileError(formatApiError(error, 'Could not load extended profile details.'));
        }
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    }

    loadRoleDetails();
    return () => {
      cancelled = true;
    };
  }, [user.email, user.role]);

  const handleChangePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setPasswordError(null);
    setPasswordMessage(null);

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }

    setPasswordSubmitting(true);
    try {
      const passwordBody = {
        current_password: currentPassword,
        new_password: newPassword,
      };
      const res =
        user.role === 'super_admin'
          ? await apiService.changeSuperAdminPassword(passwordBody)
          : await apiService.changePassword(passwordBody);
      setPasswordMessage(res.data?.message || 'Password updated successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setPasswordError(formatApiError(error, 'Could not update password.'));
    } finally {
      setPasswordSubmitting(false);
    }
  };

  const handleSendResetLink = async () => {
    setPasswordError(null);
    setPasswordMessage(null);
    setResetLinkSending(true);
    try {
      const tenantRaw = getSessionTenantId() || getHostTenantId();
      const tenantId = tenantRaw && /^\d+$/.test(tenantRaw) ? Number(tenantRaw) : undefined;
      if (!tenantId) {
        setPasswordError('Could not determine your school. Reload from your school URL and try again.');
        return;
      }
      const res = await apiService.requestPasswordReset({
        email: user.email.trim(),
        tenant_id: tenantId,
      });
      setPasswordMessage(
        res.data?.message || 'If an account exists at this school, a reset link has been sent to your email.'
      );
    } catch (error) {
      setPasswordError(formatApiError(error, 'Could not send reset link.'));
    } finally {
      setResetLinkSending(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="royal-page-header flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-secondary-500 mb-1">Account</p>
          <h1 className="royal-page-title">My profile</h1>
        </div>
        <Link to="/" className="text-sm font-medium text-primary-700 hover:text-primary-800">
          ← Back to dashboard
        </Link>
      </div>

      <div className="royal-card p-6 mb-6">
        <div className="flex flex-wrap items-start gap-5">
          {user.avatar ? (
            <img
              src={user.avatar}
              alt={user.name}
              className="h-20 w-20 rounded-full ring-2 ring-primary-400/40 object-cover"
            />
          ) : (
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-primary-500/15 to-primary-700/10 text-xl font-semibold text-primary-800 ring-2 ring-primary-400/30">
              {initials}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-semibold text-secondary-900">{user.name}</h2>
            <p className="text-sm text-secondary-600 mt-1">{user.email}</p>
            <p className="inline-flex mt-3 rounded-full border border-primary-500/20 bg-primary-500/5 px-3 py-1 text-xs font-medium capitalize text-primary-800">
              {roleLabel(user.role)}
            </p>
          </div>
        </div>

        <dl className="mt-6 grid gap-4 sm:grid-cols-2">
          <DetailRow label="User ID" value={user.id} />
          <DetailRow label="Email" value={user.email} />
          <DetailRow label="Role" value={roleLabel(user.role)} />
          {user.role !== 'super_admin' && (
            <DetailRow
              label="School / tenant"
              value={tenantLoading ? 'Loading…' : tenant?.name || '—'}
            />
          )}
          {user.linkedAdmissionNo ? (
            <DetailRow label="Admission no." value={user.linkedAdmissionNo} />
          ) : null}
        </dl>
      </div>

      {profileLoading ? (
        <div className="royal-card p-6 mb-6 text-sm text-secondary-600">Loading profile details…</div>
      ) : null}

      {profileError ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 mb-6">
          {profileError}
        </div>
      ) : null}

      {staffRecord ? (
        <div className="royal-card p-6 mb-6">
          <h2 className="text-lg font-semibold text-secondary-900 mb-4">Staff details</h2>
          <dl className="grid gap-4 sm:grid-cols-2">
            <DetailRow label="Staff no." value={staffRecord.staff_no} />
            <DetailRow
              label="Name"
              value={`${staffRecord.first_name} ${staffRecord.last_name}`.trim()}
            />
            <DetailRow label="Role" value={staffRecord.role} />
            <DetailRow label="Phone" value={staffRecord.phone} />
            <DetailRow label="Status" value={staffRecord.status} />
          </dl>
        </div>
      ) : null}

      {studentProfile ? (
        <div className="royal-card p-6 mb-6">
          <h2 className="text-lg font-semibold text-secondary-900 mb-4">Student details</h2>
          <dl className="grid gap-4 sm:grid-cols-2">
            <DetailRow label="Admission no." value={studentProfile.admission_no} />
            <DetailRow
              label="Name"
              value={`${studentProfile.first_name} ${studentProfile.last_name}`.trim()}
            />
            <DetailRow label="Class" value={studentProfile.class_name} />
            <DetailRow label="Section" value={studentProfile.section_name} />
            <DetailRow label="Status" value={studentProfile.status} />
          </dl>
        </div>
      ) : null}

      <div className="royal-card p-6">
        <h2 className="text-lg font-semibold text-secondary-900">Security</h2>
        <p className="text-sm text-secondary-600 mt-2">
          Update your password here, or send a reset link to your registered email.
        </p>

        <form onSubmit={handleChangePassword} className="mt-6 space-y-4 max-w-md">
          <div>
            <label htmlFor="current-password" className="block text-sm font-medium text-secondary-700 mb-1">
              Current password
            </label>
            <input
              id="current-password"
              type="password"
              autoComplete="current-password"
              className="royal-input w-full px-3 py-2.5 text-sm"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="profile-new-password" className="block text-sm font-medium text-secondary-700 mb-1">
              New password
            </label>
            <input
              id="profile-new-password"
              type="password"
              autoComplete="new-password"
              minLength={8}
              className="royal-input w-full px-3 py-2.5 text-sm"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="profile-confirm-password" className="block text-sm font-medium text-secondary-700 mb-1">
              Confirm new password
            </label>
            <input
              id="profile-confirm-password"
              type="password"
              autoComplete="new-password"
              minLength={8}
              className="royal-input w-full px-3 py-2.5 text-sm"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>

          {passwordError ? (
            <p className="text-sm text-red-600" role="alert">
              {passwordError}
            </p>
          ) : null}
          {passwordMessage ? (
            <p className="text-sm text-green-700" role="status">
              {passwordMessage}
            </p>
          ) : null}

          <div className="flex flex-wrap gap-3 pt-1">
            <button
              type="submit"
              disabled={passwordSubmitting || !currentPassword || !newPassword || !confirmPassword}
              className="royal-btn-primary px-4 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {passwordSubmitting ? 'Updating…' : 'Update password'}
            </button>
            <button
              type="button"
              disabled={resetLinkSending}
              onClick={handleSendResetLink}
              className="royal-btn-secondary px-4 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {resetLinkSending ? 'Sending…' : 'Email reset link instead'}
            </button>
          </div>
        </form>

        <div className="mt-8 border-t border-primary-500/10 pt-5">
          <button
            type="button"
            onClick={() => {
              logout();
              history.replace('/login');
            }}
            className="text-sm font-medium text-royal-burgundy hover:text-royal-burgundy/80"
          >
            Sign out of this account
          </button>
        </div>
      </div>
    </div>
  );
}
